import { useEffect, useState } from 'react'
import Tabs from 'components/Tabs/Tabs'
import MetadataTab from 'views/TrackView/tabs/MetadataTab'
import DesignTab from 'views/TrackView/tabs/DesignTab'
import { useWaveformQuery } from 'api/api'
import { cloneDeep, get, set } from 'lodash'
import LoadingBlocker from '../../components/LoadingSpinner/LoadingBlocker'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as PrevIcon } from 'assets/icons/chev-prev.svg'
import WaveformCanvas from '../../components/WaveformPlayer/WaveformCanvas'
import WaveformCanvasExample from '../../components/WaveformPlayer/WaveformCanvasExample'

const paths = [
  'album',
  'albumArtist',
  'art',
  'artist',
  'bpm',
  'name',
  'composer',
  'description',
  'genre',
  'grouping',
  'isrc',
  'notes',
  'releaseDate',
  'data.theme.bgColor',
  'data.theme.fgColor',
  'data.theme.highlightColor',
  'data.theme.visualizer',
  'data.theme.particleSystem.type',
]
const BulkTrackView = ({ tracks, onSubmit, onCancel, isLoading }) => {
  const { data: waveformData, isFetching: waveformDataLoading } =
    useWaveformQuery(process.env.REACT_APP_EXAMPLE_WAVEFORM)
  const [selectedTab, setSelectedTab] = useState('metadata')
  const [defaultValues, setDefaultValues] = useState(() =>
    getDefaultValues(tracks),
  )
  const [values, setValues] = useState(() => getDefaultValues(tracks))
  const [mixedValues, setMixedValues] = useState(() => getMixedValues(tracks))
  const theme = {
    bgColor: mixedValues?.data?.theme?.bgColor
      ? '#111114'
      : values?.data?.theme?.bgColor,
    fgColor: mixedValues?.data?.theme?.fgColor
      ? '#111114'
      : values?.data?.theme?.fgColor,
    highlightColor: mixedValues?.data?.theme?.highlightColor
      ? '#111114'
      : values?.data?.theme?.highlightColor,
  }

  const tabs = [
    {
      id: 'metadata',
      name: 'Metadata',
    },
    {
      id: 'design',
      name: 'Design',
    },
  ]

  useEffect(() => {
    const newValues = getDefaultValues(tracks)
    const newDefault = getDefaultValues(tracks)
    const newMixed = getMixedValues(tracks)

    paths.forEach((path) => {
      const value = get(values, path)
      const defaultValue = get(defaultValues, path)
      if (value !== defaultValue) {
        set(newValues, path, value)
        set(newMixed, path, false)
      }
    })

    setValues(newValues)
    setMixedValues(newMixed)
    setDefaultValues(newDefault)
  }, [tracks])

  useEffect(() => {
    function handleEscapeKey({ code }) {
      if (code === 'Escape') {
        onCancel()
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [])

  function handleSubmit() {
    const valuesToSubmit = {}
    paths.forEach((path) => {
      const value = get(values, path)
      if (value !== null && value !== '') {
        set(valuesToSubmit, path, value)
      }
    })

    onSubmit(valuesToSubmit)
  }

  return (
    <div className="h-full flex flex-col bg-gray relative">
      <div className="">
        <div className="px-6 pt-4 bg-gray-light">
          <button
            onClick={() => onCancel()}
            className="flex items-center justify-between mb-2"
          >
            <PrevIcon fill="#ccc" />{' '}
            <span className="block ml-2 text-sm">Back</span>
          </button>
          <h2 className="mb-4">Bulk Edit</h2>
          <h5 className="flex text-white-dark mb-0 whitespace-nowrap flex-wrap -ml-2">
            {tracks.map((track) => (
              <div
                className="px-2 mb-4 border-r border-gray-bright  last-of-type:border-r-0"
                key={track.name}
              >
                {track.name}
              </div>
            ))}
          </h5>
        </div>
      </div>
      <div className="py-10 px-6 relative">
        <WaveformCanvasExample
          waveformData={waveformData?.drawableData}
          theme={theme}
        />
        {mixedValues?.data?.theme?.highlightColor && (
          <div className="absolute top-[100px] left-[200px]">Mixed</div>
        )}
        {mixedValues?.data?.theme?.fgColor && (
          <div className="absolute top-[100px] left-[420px]">Mixed</div>
        )}
        {mixedValues?.data?.theme?.bgColor && (
          <div className="absolute top-[100px] right-[225px] font-bold">
            Mixed
          </div>
        )}
      </div>
      <div className="">
        <Tabs
          tabs={tabs}
          selectedTab={selectedTab}
          onClick={(tab) => setSelectedTab(tab.id)}
        />
      </div>
      <div className="px-8 py-6 flex-1 overflow-y-auto">
        {selectedTab === 'metadata' && (
          <MetadataTab
            values={values}
            onSubmit={handleSubmit}
            onChange={setValues}
            mixedValues={mixedValues}
            onMixedValuesChange={setMixedValues}
            isBulkEdit={true}
          />
        )}
        {selectedTab === 'design' && (
          <DesignTab
            values={values}
            onSubmit={handleSubmit}
            onChange={setValues}
            isBulkEdit={true}
            mixedValues={mixedValues}
            onMixedValuesChange={setMixedValues}
          />
        )}
      </div>

      <div className="absolute right-1 top-1">
        <button
          className="button icon bg-transparent  text-white-dark"
          onClick={onCancel}
        >
          <CloseIcon fill="#ccc" height="26" />
        </button>
      </div>
      {isLoading && <LoadingBlocker />}
    </div>
  )
}

function getMixedValues(tracks) {
  const mixedValues = {}
  paths.forEach((path) => {
    const values = tracks.map((track) => get(track, path, null))
    if (!values.every((val) => val === values[0])) {
      set(mixedValues, path, true)
    }
  })
  return mixedValues
}

function getDefaultValues(tracks) {
  const defaultValues = {}
  paths.forEach((path) => {
    const values = tracks.map((track) => get(track, path, null))
    const value = values[0]
    const otherValues = values.splice(1)
    let same = true
    otherValues.forEach((otherValue) => {
      if (otherValue !== value) {
        same = false
      }
    })
    if (same) {
      set(defaultValues, path, value)
    } else {
      set(defaultValues, path, null)
    }
  })

  return defaultValues
}

export default BulkTrackView
